import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import {
  getPermissions,
  createPermission,
  updatePermission,
  deletePermission,
} from "../../../services/PermissionService";
import { Trans, useTranslation } from "react-i18next";
import { permissionModuleLabel, permissionActionLabel, permissionDescription } from "../../../utils/accessLabels";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";

const emptyForm = {
  module: "",
  action: "",
  code: "",
  description: "",
  is_active: true,
};

const Permissions = () => {
  const { t } = useTranslation();
  const can = usePermissions();
  const canCreate = can(PERMISSIONS.PERMISSION_CREATE);
  const canUpdate = can(PERMISSIONS.PERMISSION_UPDATE);
  const canDelete = can(PERMISSIONS.PERMISSION_DELETE);
  // Data
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  // Pagination (server-side)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const limitOptions = [5, 10, 15, 20, 25, 50, 100];

  // Filtres / tri
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [sortBy, setSortBy] = useState("module");
  const [sortDir, setSortDir] = useState("asc");

  // Modal Ajout / Modification
  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [editingId, setEditingId] = useState(null);

  // Modal suppression
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Bandeau de retour
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: sortBy, sort_dir: sortDir };
      if (search) params.search = search;
      if (moduleFilter) params.module = moduleFilter;
      if (isActiveFilter !== "") params.is_active = isActiveFilter;

      const res = await getPermissions(params);
      const body = res.data;
      setPermissions(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ||
          t("pages.permissions.impossibleChargerListePermissions"),
      );
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, sortBy, sortDir, search, moduleFilter, isActiveFilter, t]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const getSortIcon = (field) => (sortBy !== field ? "↕" : sortDir === "asc" ? "↑" : "↓");

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setModuleFilter("");
    setIsActiveFilter("");
    setSortBy("module");
    setSortDir("asc");
    setPage(1);
  };

  const handleLimitChange = (value) => {
    setLimit(value);
    setPage(1);
  };

  const openAddModal = () => {
    setFormMode("add");
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError("");
    setEditingId(null);
    setShowFormModal(true);
  };

  const openEditModal = (permission) => {
    setFormMode("edit");
    setFormData({
      module: permission.module || "",
      action: permission.action || "",
      code: permission.code || "",
      description: permission.description || "",
      is_active: !!permission.is_active,
    });
    setFormErrors({});
    setSubmitError("");
    setEditingId(permission.id);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.module.trim()) errors.module = t("pages.permissions.moduleRequired");
    if (!formData.action.trim()) errors.action = t("pages.permissions.actionRequired");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        module: formData.module.trim(),
        action: formData.action.trim(),
        description: formData.description.trim() || null,
        is_active: formData.is_active,
      };
      if (formData.code.trim()) payload.code = formData.code.trim();

      if (formMode === "add") {
        await createPermission(payload);
        setFeedback({ type: "success", message: t("pages.permissions.createSuccess") });
      } else {
        await updatePermission(editingId, payload);
        setFeedback({ type: "success", message: t("pages.permissions.updateSuccess") });
      }

      setShowFormModal(false);
      fetchPermissions();
    } catch (err) {
      setSubmitError(
        err?.response?.data?.message ||
          t("common.crud.saveError"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deletePermission(deleteTarget.id);
      setFeedback({ type: "success", message: t("pages.permissions.deleteSuccess") });
      setDeleteTarget(null);
      if (permissions.length === 1 && page > 1) {
        setPage((prev) => prev - 1);
      } else {
        fetchPermissions();
      }
    } catch (err) {
      setFeedback({
        type: "danger",
        message:
          err?.response?.data?.message ||
          t("pages.permissions.deleteError"),
      });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (permission) => {
    try {
      await updatePermission(permission.id, { is_active: !permission.is_active });
      setPermissions((prev) =>
        prev.map((item) =>
          item.id === permission.id
            ? { ...item, is_active: !permission.is_active }
            : item,
        ),
      );
    } catch (err) {
      setFeedback({
        type: "danger",
        message:
          err?.response?.data?.message || t("common.crud.toggleError"),
      });
    }
  };

  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* En-tête */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("pages.permissions.breadcrumb")}
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="pe-1 mb-2">
              <button
                type="button"
                className="btn btn-outline-light bg-white btn-icon me-1"
                title={t("common.crud.refresh")}
                onClick={resetFilters}
              >
                <i className="ti ti-refresh"></i>
              </button>
            </div>
            {canCreate && (
<div className="mb-2">
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center"
                onClick={openAddModal}
              >
                <i className="ti ti-square-rounded-plus me-2"></i>{t("pages.permissions.addLabel")}
              </button>
            </div>
)}
          </div>
        </div>

        {feedback && (
          <div
            className={`alert alert-${feedback.type} alert-dismissible fade show`}
            role="alert"
          >
            {feedback.message}
            <button
              type="button"
              className="btn-close"
              onClick={() => setFeedback(null)}
            ></button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("pages.permissions.title")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="input-icon-start mb-3 me-2 position-relative">
                <span className="icon-addon"></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.permissions.searchPlaceholder")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ paddingLeft: "35px", minWidth: "220px" }}
                />
              </div>
              <div className="mb-3 me-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.permissions.modulePlaceholder")}
                  value={moduleFilter}
                  onChange={(e) => {
                    setModuleFilter(e.target.value);
                    setPage(1);
                  }}
                  style={{ minWidth: "160px" }}
                />
              </div>
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={isActiveFilter}
                  onChange={(e) => {
                    setIsActiveFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t("common.crud.allStatuses")}</option>
                  <option value="true">{t("common.ui.actives")}</option>
                  <option value="false">{t("common.ui.inactives")}</option>
                </select>
              </div>
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center justify-content-between px-3 py-2 border-bottom">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <span className="text-sm text-gray-600">
                <Trans i18nKey="common.pagination.showingEntries" values={{ start, end, total }} components={[<span className="font-semibold text-gray-900" />, <span className="font-semibold text-gray-900" />, <span className="font-semibold text-gray-900" />]} />
              </span>
              <span className="text-gray-300">|</span>
              <div className="d-flex align-items-center gap-2">
                <span className="text-sm text-gray-600">
                  {t("common.pagination.rowsPerPage")}
                </span>
                <Dropdown className="drop-select">
                  <Dropdown.Toggle
                    as="div"
                    className="drop-select-btn"
                    style={{
                      minWidth: "70px",
                      background: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      padding: "4px 12px",
                      cursor: "pointer",
                    }}
                  >
                    {limit}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {limitOptions.map((option) => (
                      <Dropdown.Item
                        key={option}
                        onClick={() => handleLimitChange(option)}
                        className={limit === option ? "active" : ""}
                      >
                        {option}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </div>

          <div className="card-body p-0 py-3">
            <div className="custom-datatable-filter table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th onClick={() => handleSort("module")} style={{ cursor: "pointer" }}>
                      {t("common.fields.module")} {getSortIcon("module")}
                    </th>
                    <th>{t("common.crud.actionColumn")}</th>
                    <th onClick={() => handleSort("code")} style={{ cursor: "pointer" }}>
                      {t("common.fields.code")} {getSortIcon("code")}
                    </th>
                    <th>{t("common.fields.description")}</th>
                    <th>{t("common.crud.statusColumn")}</th>
                    <th>{t("common.crud.actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <div className="text-danger">{loadError}</div>
                      </td>
                    </tr>
                  ) : permissions.length > 0 ? (
                    permissions.map((permission) => (
                      <tr key={permission.id}>
                        <td>
                          <span className="badge badge-soft-info">
                            {permissionModuleLabel(t, permission.module)}
                          </span>
                        </td>
                        <td>{permissionActionLabel(t, permission.action)}</td>
                        <td>
                          <span className="fw-medium">{permission.code}</span>
                        </td>
                        <td>
                          <span
                            className="text-truncate d-inline-block"
                            style={{ maxWidth: "220px" }}
                            title={permissionDescription(t, permission)}
                          >
                            {permissionDescription(t, permission) || "-"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge badge-soft-${permission.is_active ? "success" : "danger"} d-inline-flex align-items-center`}
                            role="button"
                            onClick={() => toggleActive(permission)}
                            title={t("common.crud.toggleStatusHint")}
                          >
                            <i className="ti ti-circle-filled fs-5 me-1"></i>
                            {permission.is_active ? t("common.ui.active") : t("common.ui.inactive")}
                          </span>
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              as="button"
                              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false"
                            >
                              <i className="ti ti-dots-vertical fs-14"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="p-3">
                              {canUpdate && (
<Dropdown.Item
                                className="rounded-1"
                                onClick={() => openEditModal(permission)}
                              >
                                <i className="ti ti-edit-circle me-2"></i>
                                {t("common.crud.edit")}
                              </Dropdown.Item>
)}
                              <Dropdown.Item
                                className="rounded-1"
                                onClick={() => toggleActive(permission)}
                              >
                                <i className="ti ti-toggle-right me-2"></i>
                                {permission.is_active ? t("common.crud.deactivate") : t("common.crud.activate")}
                              </Dropdown.Item>
                              {canDelete && (
<Dropdown.Item
                                className="rounded-1"
                                onClick={() => setDeleteTarget(permission)}
                              >
                                <i className="ti ti-trash-x me-2"></i>
                                {t("common.crud.delete")}
                              </Dropdown.Item>
)}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <div className="text-center">
                          <i className="ti ti-search-off fs-1 text-muted"></i>
                          <h5 className="mt-2">{t("pages.permissions.aucunePermissionTrouvee")}</h5>
                          <p className="text-muted">
                            {t("common.ui.essayezModifierVosCriteresRecherche")}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex flex-wrap align-items-center justify-content-between px-3 pt-3 border-top">
              <div className="text-sm text-gray-600">
                <Trans i18nKey="common.pagination.showingEntries" values={{ start, end, total }} components={[<span className="font-semibold text-gray-900" />, <span className="font-semibold text-gray-900" />, <span className="font-semibold text-gray-900" />]} />
              </div>

              {total > 0 && (
                <div className="flex items-center gap-1">
                  <button
                    className={`px-3 py-1 rounded-md border ${page === 1 ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-100"}`}
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                    disabled={page === 1}
                  >
                    <i className="fa-solid fa-angle-left"></i>
                  </button>
                  {[...Array(totalPages).keys()].map((n) => {
                    const pageNum = n + 1;
                    const totalPagesToShow = 5;
                    const halfWay = Math.ceil(totalPagesToShow / 2);
                    const startPage = Math.max(
                      1,
                      Math.min(page - halfWay + 1, totalPages - totalPagesToShow + 1),
                    );
                    const endPage = Math.min(totalPages, startPage + totalPagesToShow - 1);

                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= startPage && pageNum <= endPage)
                    ) {
                      return (
                        <button
                          key={n}
                          className={`px-3 py-1 rounded-md border ${page === pageNum ? "bg-primary text-white" : "text-gray-700 hover:bg-gray-100"}`}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (pageNum === startPage - 1 || pageNum === endPage + 1) {
                      return (
                        <span key={n} className="px-2 text-gray-400">
                          …
                        </span>
                      );
                    }
                    return null;
                  })}
                  <button
                    className={`px-3 py-1 rounded-md border ${page === totalPages ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-100"}`}
                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={page === totalPages}
                  >
                    <i className="fa-solid fa-angle-right"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal Ajout / Modification */}
      <Modal show={showFormModal} onHide={closeFormModal} centered>
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {formMode === "add" ? t("pages.permissions.addLabel") : t("pages.permissions.editTitle")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && (
              <div className="alert alert-danger">{submitError}</div>
            )}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  {t("common.fields.module")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${formErrors.module ? "is-invalid" : ""}`}
                  placeholder="EXAM"
                  value={formData.module}
                  onChange={(e) => handleFormChange("module", e.target.value)}
                />
                {formErrors.module && (
                  <div className="invalid-feedback">{formErrors.module}</div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  {t("common.crud.actionColumn")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${formErrors.action ? "is-invalid" : ""}`}
                  placeholder="ARCHIVE"
                  value={formData.action}
                  onChange={(e) => handleFormChange("action", e.target.value)}
                />
                {formErrors.action && (
                  <div className="invalid-feedback">{formErrors.action}</div>
                )}
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">
                <Trans i18nKey="pages.permissions.codeGenereAutomatiquementSiLaisseVide" components={[<span className="text-muted fw-normal" />]} />
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="EXAM_ARCHIVE"
                value={formData.code}
                onChange={(e) => handleFormChange("code", e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">{t("common.fields.description")}</label>
              <textarea
                className="form-control"
                rows={2}
                value={formData.description}
                onChange={(e) => handleFormChange("description", e.target.value)}
              ></textarea>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="permission-is-active"
                checked={formData.is_active}
                onChange={(e) => handleFormChange("is_active", e.target.checked)}
              />
              <label className="form-check-label" htmlFor="permission-is-active">
                {t("pages.permissions.activeLabel")}
              </label>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-light"
              onClick={closeFormModal}
              disabled={submitting}
            >
              {t("common.crud.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting
                ? t("common.crud.saving")
                : formMode === "add"
                  ? t("common.crud.add")
                  : t("common.crud.save")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Modal Suppression */}
      <Modal
        show={deleteTarget !== null}
        onHide={() => (deleting ? null : setDeleteTarget(null))}
        centered
      >
        <div className="modal-body text-center">
          <span className="delete-icon">
            <i className="ti ti-trash-x"></i>
          </span>
          <h4>{t("common.crud.confirmDeleteTitle")}</h4>
          <p>
            <Trans i18nKey="pages.permissions.voulezVousVraimentSupprimerPermissionAction" values={{ code: deleteTarget?.code }} components={[<strong />]} />
          </p>
          <div className="d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-light me-3"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              {t("common.crud.cancel")}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? t("common.crud.deleting") : t("common.crud.confirmDelete")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Permissions;